import React, { useEffect, useMemo, useRef, useState } from "react";
import Axios from "axios";
import { Host } from "../../../../App";

export default function RigidSchedulePage({ postList, onSchedule }) {
  const [poStart, setPoStart] = useState();
  const [poEnd, setPoEnd] = useState("2026-12-31");
  const [customer, setCustomer] = useState("");
  const [partDesc, setPartDesc] = useState("");
  const token = window.localStorage.getItem("acessToken");
  const [employees, setEmployees] = useState([]);
  const [Tentative, setTentative] = useState([]);
  const [Internal_Part_No, setInternal_Part_No] = useState("");
  const [allData, setallData] = useState([]);
  const printRef = useRef(null);
  const queryParams = { token };

  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);

  useEffect(() => {
    Axios.get(`https://${Host}/realapi/Production/daily_data_records`, { params: queryParams })
      .then((res) => setallData(res.data))
      .catch((err) => console.error("Error fetching production schedule:", err));
  }, []);

  useEffect(() => {
    if (postList && postList.length) return;
    const params = { enddate: poEnd, startdate: poStart, Customer: customer, Part_Desc: partDesc, token };
    if (!poStart) {
      Axios.get(`https://${Host}/latestdate/Inventory`, { params })
        .then((res) => {
          const iso = res.data?.[0]?.latest;
          if (iso) setPoStart(iso.slice(0, 10));
        })
        .catch(() => { });
    }
  }, [postList, poStart, poEnd, customer, partDesc, token]);

  useEffect(() => {
    Axios.get(`https://${Host}/api/employees_all/${token}`)
      .then((data) => setEmployees(data.data || []))
      .catch(() => setEmployees([]));
  }, [token]);

  const [selected, setSelected] = useState(null);
  const [chunkModal, setChunkModal] = useState({
    open: false, rows: [], r: null, date: "", pending: 0, chunkSize: 0,
  });

  const Tentative_call = (Internal_part_no, qty) => {
    Axios.get(`https://${Host}/api/Tentative_Numbers`, {
      params: { Internal_part_no, qty, token },
    })
      .then((data) => setInternal_Part_No(data.data[0]?.max || 0))
      .catch((err) => { console.error("Tentative API error:", err); setInternal_Part_No(0); });
  };

  useEffect(() => {
    Axios.get(`https://${Host}/api/Tentative_schedule/${token}`)
      .then((data) => setTentative(data.data))
      .catch(() => setTentative([]));
  }, [token]);

  useEffect(() => {
    if (!selected || Internal_Part_No === "") return;
    const apiSize = Number(Internal_Part_No) || 0;
    const pending = Number(selected.pending || 0);
    const keyPrefix = `${selected.date}|${selected.r.internalPart}`;
    const rows = buildChunkRows(pending, apiSize, keyPrefix);
    setChunkModal({ open: true, rows, r: selected.r, date: selected.date, pending, chunkSize: apiSize });
    applyRowsToSchedule(rows, todayISO, "", selected.r.internalPart);
  }, [Internal_Part_No, selected]);

  const handlePrint = () => {
    if (!printRef.current) return;
    const win = window.open("", "", "height=900,width=1200");
    win.document.write("<html><head><title>Print -R Required By Date</title>");
    win.document.write('<link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.3.1/css/bootstrap.min.css"/>');
    win.document.write("</head><body>");
    win.document.write(printRef.current.innerHTML);
    win.document.write("</body></html>");
    win.document.close();
    win.focus();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Utilities
  // ─────────────────────────────────────────────────────────────────────────────
  const normalise = (s) => (s || "").replace(/\s+/g, " ").trim();

  // Parse "PART NAME -R (123)" — greedy so part names containing " - " work
  const parseREntry = (entry) => {
    const s = normalise(entry);
    const match = s.match(/^(.*\s-R)\s*\(\s*(-?\d+)\s*\)$/i);
    if (!match) return null;
    const internalPart = normalise(match[1]);
    if (!/\s-R$/i.test(internalPart)) return null;
    return { internalPart, qty: parseInt(match[2], 10) };
  };

  // Parse "Main Part - PART NAME -R (33)" from Pending_Parts_List
  const parsePendingEntry = (entry) => {
    const s = normalise(entry);
    const stripped = s.replace(/^[\w\s]+ Part\s*-\s*/i, "");
    const match = stripped.match(/^(.*\s-R)\s*\(\s*(-?\d+)\s*\)$/i);
    if (!match) return null;
    const internalPart = normalise(match[1]);
    if (!/\s-R$/i.test(internalPart)) return null;
    return { internalPart, pending: parseInt(match[2], 10) };
  };

  // Parse PO_Details → [{ date, qty }, ...] sorted ascending
  const parsePoDetails = (poStr) => {
    if (!poStr) return [];
    return poStr
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .reduce((acc, seg) => {
        const m = seg.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s*-\s*(-?\d+)/);
        if (m) {
          const [, mm, dd, yyyy, q] = m;
          const qty = Math.max(0, parseInt(q, 10));
          if (qty > 0)
            acc.push({
              date: `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`,
              qty,
            });
        }
        return acc;
      }, [])
      .sort((a, b) => a.date.localeCompare(b.date));
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 1 — Aggregates: future tentative & today-onward unfinished
  // ─────────────────────────────────────────────────────────────────────────────
  const partAggregates = useMemo(() => {
    const futureTentativeByInternal = {};
    (Tentative || []).forEach((t) => {
      const schedDate = (t.Date || "").slice(0, 10);
      if (schedDate < todayISO) return;
      const key = normalise(
        t.Internal_Part_No || t.internal_part_no || t.internalPartNo || ""
      ).toUpperCase();
      const qty = Math.max(0, Number(t.QTY ?? t.qty ?? 0) || 0);
      if (key && qty > 0)
        futureTentativeByInternal[key] = (futureTentativeByInternal[key] || 0) + qty;
    });

    const unfinishedByInternal = {};
    (allData || []).forEach((row) => {
      const rowDate = (row.Date || row.date || "").slice(0, 10);
      if (rowDate < todayISO) return;
      const key = normalise(
        row.Part_internalNo || row.Internal_Part_No || row.internal_part_no || ""
      ).toUpperCase();
      const remain = Math.max(
        0,
        (Number(row.Target ?? 0) || 0) - (Number(row.Produced ?? 0) || 0)
      );
      if (key && remain > 0)
        unfinishedByInternal[key] = (unfinishedByInternal[key] || 0) + remain;
    });

    return { futureTentativeByInternal, unfinishedByInternal };
  }, [Tentative, allData, todayISO]);

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 2 — Build shortage rows
  //
  //  HOW MANY:  taken directly from SQL Required_Ship_QTY_List — never changed.
  //             Only adjusted for future tentative and unfinished.
  //
  //  WHICH DATE: PO walk using SQL's Pending_Parts as available stock.
  //
  //  Walk logic (key insight):
  //    The shortage of `requiredQty` falls on the LAST PO dates — because the
  //    existing stock (sqlPending) covers the earliest POs first.
  //    So we skip the first (totalPO - requiredQty) units of PO demand,
  //    then place the shortage on the remaining PO dates.
  //
  //    demandThreshold = totalPO - requiredQty
  //    Walk PO dates:
  //      • while demandThreshold >= poQty → this date is fully covered, skip
  //      • once demandThreshold < poQty  → shortage starts here
  //
  //  Ellaby verification:
  //    totalPO=48, requiredQty=2, demandThreshold=46
  //    3/16(12): 46≥12 → skip, threshold=34
  //    3/19(12): 34≥12 → skip, threshold=22
  //    4/1 (12): 22≥12 → skip, threshold=10
  //    4/13( 6): 10≥6  → skip, threshold=4
  //    4/14( 6): 4<6   → shortage = min(6-4, 2) = 2 ✓ on 4/14
  // ─────────────────────────────────────────────────────────────────────────────
  const parseRequiredParts = useMemo(() => {
    if (!postList || postList.length === 0) return [];
    const results = [];

    postList.forEach((row) => {
      const requiredList = normalise(row.Required_Ship_QTY_List || "");
      if (!requiredList) return;

      // All -R entries — SQL's required qty, never recalculated
      const reqEntries = requiredList
        .split(/\)\s*,\s*/)
        .map((e, i, arr) => (i < arr.length - 1 ? e + ")" : e))
        .map(parseREntry)
        .filter((e) => e && e.qty > 0);

      if (reqEntries.length === 0) return;

      // Build pendingMap from Pending_Parts_List (SQL stock, used for date placement only)
      const pendingMap = {};
      (row.Pending_Parts_List || "")
        .split(/\)\s*,\s*/)
        .map((e, i, arr) => (i < arr.length - 1 ? e + ")" : e))
        .forEach((entry) => {
          const p = parsePendingEntry(entry);
          if (p) pendingMap[normalise(p.internalPart).toUpperCase()] = p.pending;
        });

      const poDateEntries = parsePoDetails(row.PO_Details); // sorted asc
      const totalPoQty = poDateEntries.reduce((s, x) => s + x.qty, 0);

      reqEntries.forEach(({ internalPart, qty: sqlRequiredQty }) => {
        const key = normalise(internalPart).toUpperCase();
        const futureTent = partAggregates.futureTentativeByInternal[key] || 0;
        const unfinished = partAggregates.unfinishedByInternal[key] || 0;

        // HOW MANY to schedule — SQL value adjusted only for tentative/unfinished
        // Hard cap: never exceeds sqlRequiredQty
        const pendingToSchedule = Math.min(
          sqlRequiredQty,
          Math.max(0, sqlRequiredQty - futureTent + unfinished)
        );

        if (pendingToSchedule <= 0) return; // fully covered by future tentative

        // WHICH DATE — walk PO dates to find where the shortage falls
        if (poDateEntries.length === 0) {
          results.push({
            internalPart,
            description: row.Part_description || "",
            customer: row.Customer || "",
            requiredQty: sqlRequiredQty,
            shortageQty: pendingToSchedule,
            poDate: null,
            poQty: totalPoQty,
            poDetails: row.PO_Details || "",
            futureTentative: futureTent,
            unfinished,
            sourceRow: row,
          });
          return;
        }

        // demandThreshold = the amount of PO demand covered by existing stock
        // (totalPO - requiredQty) = how much stock covers before shortage starts
        // We walk forward skipping covered dates, then place shortage on last dates
        let demandThreshold = totalPoQty - sqlRequiredQty;
        let remainingShortage = pendingToSchedule;

        for (const { date: poDate, qty: poQty } of poDateEntries) {
          if (remainingShortage <= 0) break;

          if (demandThreshold >= poQty) {
            // This entire PO date is covered by existing stock — skip
            demandThreshold -= poQty;
          } else {
            // Shortage begins somewhere in this PO date
            // Only the portion beyond demandThreshold is a shortage
            const uncoveredInThisDate = poQty - demandThreshold;
            const shortageHere = Math.min(uncoveredInThisDate, remainingShortage);
            demandThreshold = 0; // threshold exhausted after first shortage date

            results.push({
              internalPart,
              description: row.Part_description || "",
              customer: row.Customer || "",
              requiredQty: sqlRequiredQty,
              shortageQty: shortageHere,
              poDate,
              poQty: totalPoQty,
              poDetails: row.PO_Details || "",
              futureTentative: futureTent,
              unfinished,
              sourceRow: row,
            });

            remainingShortage -= shortageHere;
          }
        }
      });
    });

    return results;
  }, [postList, partAggregates]);

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 3 — Group by PO shortage date
  // ─────────────────────────────────────────────────────────────────────────────
  const groupedByDate = useMemo(() => {
    const groups = {};
    parseRequiredParts.forEach((part) => {
      const dateKey = part.poDate || "No Date";
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(part);
    });
    return Object.fromEntries(
      Object.entries(groups).sort(([a], [b]) => {
        if (a === "No Date") return 1;
        if (b === "No Date") return -1;
        return a.localeCompare(b);
      })
    );
  }, [parseRequiredParts]);

  const dates = useMemo(() => Object.keys(groupedByDate), [groupedByDate]);

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 4 — Grand totals (de-duplicate requiredQty per part+customer)
  // ─────────────────────────────────────────────────────────────────────────────
  const grandTotals = useMemo(() => {
    const seenReq = new Set();
    let totalRequired = 0;
    let totalFutureTent = 0;
    let totalUnfinished = 0;
    let totalPending = 0;

    parseRequiredParts.forEach((p) => {
      const key = `${p.customer}|${p.internalPart}`;
      if (!seenReq.has(key)) {
        seenReq.add(key);
        totalRequired += p.requiredQty;
        totalFutureTent += p.futureTentative;
        totalUnfinished += p.unfinished;
      }
      totalPending += p.shortageQty;
    });

    return { totalRequired, totalFutureTent, totalUnfinished, totalPending };
  }, [parseRequiredParts]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────────
  const alignDescWithInternal = (internal) =>
    (internal || "").replace(/\s*-R\s*$/i, "").replace(/\s+/g, " ").trim();

  const isWeekendISO = (iso) => {
    const [y, m, d] = (iso || "").split("-").map(Number);
    if (!y || !m || !d) return false;
    return [0, 6].includes(new Date(y, m - 1, d).getDay());
  };

  const addDaysISO = (iso, days) => {
    const dt = new Date(`${iso}T00:00:00`);
    dt.setDate(dt.getDate() + days);
    return dt.toISOString().slice(0, 10);
  };

  const nextBusinessDayISO = (iso) => {
    let cur = addDaysISO(iso, 1);
    while (isWeekendISO(cur)) cur = addDaysISO(cur, 1);
    return cur;
  };

  const businessSeries = (startISO, count) => {
    let cur = startISO;
    while (isWeekendISO(cur)) cur = addDaysISO(cur, 1);
    const out = [cur];
    for (let i = 1; i < count; i++) { cur = nextBusinessDayISO(cur); out.push(cur); }
    return out;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Scheduling state
  // ─────────────────────────────────────────────────────────────────────────────
  const [showSchedule, setShowSchedule] = useState(false);
  const [sched, setSched] = useState({});

  const setField = (key, field, value) =>
    setSched((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || { employee: "", date: "", qty: "", internalPartNo: "" }),
        [field]: value,
      },
    }));

  const tentativeIdsRef = useRef({});
  const perPartCountersRef = useRef({});

  const getTentativeId = (row, suffix = "") => {
    const baseKey = (row.internalPart || "NA").trim() || "NA";
    const mapKey = suffix ? `${baseKey}::${suffix}` : baseKey;
    if (tentativeIdsRef.current[mapKey]) return tentativeIdsRef.current[mapKey];
    perPartCountersRef.current[baseKey] = (perPartCountersRef.current[baseKey] || 0) + 1;
    const ts = new Date().toISOString().replace(/[-:.TZ]/g, "");
    const id = `${baseKey}-${ts}-${perPartCountersRef.current[baseKey]}${suffix ? `-${suffix}` : ""}`;
    tentativeIdsRef.current[mapKey] = id;
    return id;
  };

  const handleRowSchedule = (key, r) => {
    const data = sched[key] || {};
    const chosenInternal = (data.internalPartNo || r.internalPart || "").trim();
    const payload = {
      employee: (data.employee || "").trim(),
      date: (data.date || "").trim(),
      qty: Number(data.qty || 0),
      internalPartNo: chosenInternal,
      description: alignDescWithInternal(chosenInternal),
      customer: r.customer,
      poQty: Number(r.poQty || 0),
      shortage: Number(r.shortageQty || 0),
      totalRequired: r.requiredQty,
      Tentative_ID: getTentativeId(r),
      poDate: r.poDate || "",
    };
    Axios.post(`https://${Host}/Rigid/tentative/add`, { changedData: JSON.stringify(payload), token })
      .then((res) => alert(res.data?.message || "Saved."))
      .catch((err) => alert(err?.response?.data?.message || err?.message || "Something went wrong."));
    if (onSchedule) onSchedule(payload);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Chunk / split modal
  // ─────────────────────────────────────────────────────────────────────────────
  const buildChunkRows = (pending, size, keyPrefix) => {
    const n = size > 0 ? Math.ceil(pending / size) : pending > 0 ? 1 : 0;
    let remaining = pending;
    const rows = [];
    for (let i = 0; i < n; i++) {
      const q = size > 0 ? Math.min(size, remaining) : remaining;
      remaining -= q;
      rows.push({ key: `${keyPrefix}|chunk${i}`, qty: q, idx: i });
    }
    return rows;
  };

  const applyRowsToSchedule = (rows, startDateISO, employee, defaultInternal) => {
    const series = businessSeries(startDateISO || todayISO, rows.length);
    setSched((prev) => {
      const next = { ...prev };
      const newKeys = new Set(rows.map((r) => r.key));
      Object.keys(next).forEach((k) => {
        if (k.includes("|chunk") && !newKeys.has(k)) delete next[k];
      });
      rows.forEach(({ key, qty }, idx) => {
        next[key] = {
          ...(next[key] || {}),
          employee: employee ?? next[key]?.employee ?? "",
          date: series[idx],
          qty,
          internalPartNo: next[key]?.internalPartNo || defaultInternal,
        };
      });
      return next;
    });
  };

  const handleSplitSizeChange = (val) => {
    const size = Math.max(0, Number(val) || 0);
    const first = chunkModal.rows[0];
    const firstState = first ? sched[first.key] || {} : {};
    const keyPrefix = `${chunkModal.date}|${chunkModal.r?.internalPart}`;
    const newRows = buildChunkRows(chunkModal.pending, size, keyPrefix);
    setChunkModal((cm) => ({ ...cm, rows: newRows, chunkSize: size }));
    applyRowsToSchedule(newRows, firstState.date || todayISO, firstState.employee || "", firstState.internalPartNo || chunkModal.r?.internalPart || "");
  };

  const handlePendingChange = (val) => {
    const pending = Math.max(0, Number(val) || 0);
    const size = Math.max(0, Number(chunkModal.chunkSize) || 0);
    const first = chunkModal.rows[0];
    const firstState = first ? sched[first.key] || {} : {};
    const keyPrefix = `${chunkModal.date}|${chunkModal.r?.internalPart}`;
    const newRows = buildChunkRows(pending, size, keyPrefix);
    setChunkModal((cm) => ({ ...cm, rows: newRows, pending }));
    applyRowsToSchedule(newRows, firstState.date || todayISO, firstState.employee || "", firstState.internalPartNo || chunkModal.r?.internalPart || "");
  };

  const handleScheduleAll = () => {
    const rows = chunkModal.rows.map(({ key }, idx) => {
      const s = sched[key] || {};
      const r = chunkModal.r || {};
      return {
        employee: (s.employee || "").trim(),
        date: (s.date || "").trim(),
        qty: Number(s.qty || 0),
        internalPartNo: (s.internalPartNo || r.internalPart || "").trim(),
        description: alignDescWithInternal(s.internalPartNo || r.internalPart),
        customer: r.customer,
        Tentative_ID: getTentativeId(r, String(idx + 1)),
        poDate: r.poDate || "",
      };
    });
    Axios.post(`https://${Host}/Rigid/tentative/add`, { changedData: rows, token })
      .then((res) => alert(res.data?.message || "Saved."))
      .catch((err) => alert(err?.response?.data?.message || err?.message || "Something went wrong."));
  };

  const handleInternalPartClick = (date, r) => {
    setSelected({ date, r, pending: r.shortageQty });
    setInternal_Part_No("");
    Tentative_call(r.internalPart, r.shortageQty);
  };

  const closeModal = () => {
    setChunkModal({ open: false, rows: [], r: null, date: "", pending: 0, chunkSize: 0 });
    setSelected(null);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid py-3" style={{ maxHeight: 900, overflowY: "auto" }}>

      {/* ── Header ── */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="m-0">Rigid -R Parts Requirements</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-primary" onClick={handlePrint}>Print</button>
          <button className="btn btn-outline-secondary" onClick={() => setShowSchedule((s) => !s)}>
            {showSchedule ? "Hide Schedule" : "Add Schedule"}
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      {!postList && (
        <div className="row g-2 mb-3">
          <div className="col">
            <label className="form-label mb-1">Start</label>
            <input type="date" className="form-control form-control-sm" value={poStart || ""}
              onChange={(e) => setPoStart(e.target.value)} />
          </div>
          <div className="col">
            <label className="form-label mb-1">End</label>
            <input type="date" className="form-control form-control-sm" value={poEnd}
              onChange={(e) => setPoEnd(e.target.value)} />
          </div>
          <div className="col">
            <label className="form-label mb-1">Customer</label>
            <input type="text" className="form-control form-control-sm" value={customer}
              onChange={(e) => setCustomer(e.target.value)} placeholder="Customer" />
          </div>
          <div className="col">
            <label className="form-label mb-1">Part Description</label>
            <input type="text" className="form-control form-control-sm" value={partDesc}
              onChange={(e) => setPartDesc(e.target.value)} placeholder="Part Description" />
          </div>
        </div>
      )}

      {showSchedule && employees?.length > 0 && (
        <datalist id="employees">
          {employees.map((e) => <option key={e.Emp_ID || e} value={e.Name || e} />)}
        </datalist>
      )}

      <div ref={printRef}>

        {/* ── Status banner ── */}
        {parseRequiredParts.length === 0 ? (
          <div className="alert alert-warning">
            <strong>No rigid -R parts found with required quantity &gt; 0</strong>
            <div className="mt-2">
              <small>The SQL query returned {postList?.length || 0} rows but none contain -R shortages.</small>
            </div>
          </div>
        ) : (
          <div className="alert alert-success mb-3">
            Showing {parseRequiredParts.length} shortage row(s) across {dates.length} PO date(s).
            &nbsp;<small className="text-muted">(Tentative counted from {todayISO} onward only)</small>
          </div>
        )}

        {/* ── Per-date tables ── */}
        {dates.map((date) => {
          const parts = groupedByDate[date];
          if (!parts || parts.length === 0) return null;
          const dateShortageTotal = parts.reduce((s, p) => s + p.shortageQty, 0);

          return (
            <div key={date} className="mb-4">
              <h5 className="mb-2 d-flex align-items-center gap-2">
                {date === "No Date" ? "Parts without PO date" : `PO Date: ${date}`}
                <span className="badge bg-danger" style={{ fontSize: "0.8rem", color: "white" }}>
                  Shortage: {dateShortageTotal}
                </span>
              </h5>

              <table className="table table-bordered table-striped table-sm">
                <thead className="table-dark">
                  <tr>
                    <th>Internal Part No</th>
                    <th>Description</th>
                    <th>Customer</th>
                    <th>PO QTY</th>
                    <th>Total Req -R</th>
                    <th title="Future-dated tentative schedules >= today">Fut. Tent. ↓</th>
                    <th title="Today-onward unfinished jobs not yet produced — added back">Unfinished ↑</th>
                    <th title="Shortage qty falling on this PO date">Pending Sched.</th>
                    {showSchedule && (
                      <>
                        <th>Employee</th>
                        <th>Date</th>
                        <th>QTY</th>
                        <th>Schedule</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {parts.map((r, i) => {
                    const rowKey = `${date}|${r.internalPart}|${i}`;
                    const rowState = sched[rowKey] || {
                      employee: "", date: "", qty: "", internalPartNo: r.internalPart,
                    };
                    return (
                      <tr key={rowKey}>
                        <td>
                          <button className="btn btn-outline-primary btn-sm"
                            onClick={() => handleInternalPartClick(date, r)}>
                            {r.internalPart}
                          </button>
                        </td>
                        <td>{alignDescWithInternal(r.internalPart)}</td>
                        <td>{r.customer}</td>
                        <td>{r.poQty}</td>
                        <td><strong>{r.requiredQty}</strong></td>
                        <td>
                          {r.futureTentative > 0
                            ? <span className="badge bg-info text-dark">{r.futureTentative}</span>
                            : <span className="text-muted">0</span>}
                        </td>
                        <td>
                          {r.unfinished > 0
                            ? <span className="badge bg-secondary">{r.unfinished}</span>
                            : <span className="text-muted">0</span>}
                        </td>
                        <td>
                          {r.shortageQty > 0
                            ? <span className="badge bg-warning text-dark fs-6">{r.shortageQty}</span>
                            : <span className="text-success fw-bold">✓ 0</span>}
                        </td>
                        {showSchedule && (
                          <>
                            <td style={{ minWidth: 180 }}>
                              <input className="form-control form-control-sm"
                                placeholder="Select or type"
                                value={rowState.employee} list="employees"
                                onChange={(e) => setField(rowKey, "employee", e.target.value)} />
                            </td>
                            <td style={{ minWidth: 150 }}>
                              <input type="date" className="form-control form-control-sm"
                                value={rowState.date}
                                onChange={(e) => setField(rowKey, "date", e.target.value)} />
                            </td>
                            <td style={{ width: 150 }}>
                              <input type="number" min={0} step={1}
                                className="form-control form-control-sm"
                                value={rowState.qty}
                                placeholder={String(r.shortageQty || 0)}
                                onChange={(e) => setField(rowKey, "qty", e.target.value)} />
                            </td>
                            <td style={{ width: 120 }}>
                              <button className="btn btn-sm btn-success"
                                onClick={() => handleRowSchedule(rowKey, r)}>
                                Schedule
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="table-secondary fw-bold">
                    <td colSpan={7} className="text-end">
                      Total shortage on {date === "No Date" ? "No Date" : date}:
                    </td>
                    <td><span className="badge bg-danger" style={{ fontSize: "0.8rem", color: "white" }}>{dateShortageTotal}</span></td>
                    {showSchedule && <td colSpan={4} />}
                  </tr>
                </tfoot>
              </table>
            </div>
          );
        })}

        {/* ── Grand Total ── */}
        {parseRequiredParts.length > 0 && (
          <div className="mb-4">
            <h5 className="mb-2">Grand Total — All -R Parts</h5>
            <table className="table table-bordered table-sm" style={{ maxWidth: 560 }}>
              <thead className="table-dark">
                <tr><th>Metric</th><th className="text-end">QTY</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total Required -R Parts</td>
                  <td className="text-end fw-bold text-danger">{grandTotals.totalRequired}</td>
                </tr>
                <tr>
                  <td>Future Tentative Scheduled <small className="text-muted">(&ge; {todayISO})</small></td>
                  <td className="text-end fw-bold text-info">{grandTotals.totalFutureTent}</td>
                </tr>
                <tr>
                  <td>Unfinished In-Progress <small className="text-muted">(today+ not yet produced)</small></td>
                  <td className="text-end fw-bold text-secondary">{grandTotals.totalUnfinished}</td>
                </tr>
                <tr className="table-warning">
                  <td>
                    <strong>Total Pending Schedule</strong>
                    <small className="text-muted ms-2">(sum of all date shortages)</small>
                  </td>
                  <td className="text-end fw-bold">{grandTotals.totalPending}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Chunk / Split Modal ── */}
      {chunkModal.open && (
        <>
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 1050 }}
            onClick={closeModal} />
          <div className="card shadow" style={{
            position: "fixed", zIndex: 1060, top: "8%", left: "50%",
            transform: "translateX(-50%)", width: "min(1100px, 95vw)",
            overflowY: "auto", maxHeight: "80vh",
          }}>
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <strong>Schedule: {chunkModal.r?.internalPart}</strong>
                <div className="small text-muted">
                  Shortage: {chunkModal.pending} | Rows: {chunkModal.rows.length}
                </div>
              </div>
              <button className="btn btn-sm btn-outline-secondary" onClick={closeModal}>Close</button>
            </div>

            <div className="card-body">
              <div className="d-flex align-items-center mb-3 flex-wrap" style={{ gap: 12 }}>
                <label className="mb-0">Qty to schedule:</label>
                <input type="number" min={0} step={1} className="form-control form-control-sm"
                  style={{ width: 110 }} value={chunkModal.pending || ""}
                  onChange={(e) => handlePendingChange(e.target.value)} />
                <label className="mb-0 ms-2">Split size:</label>
                <input type="number" min={0} step={1} className="form-control form-control-sm"
                  style={{ width: 110 }} value={chunkModal.chunkSize || ""}
                  onChange={(e) => handleSplitSizeChange(e.target.value)} />
                <span className="text-muted small">0 or blank = single row</span>
              </div>

              {employees?.length > 0 && (
                <datalist id="employees_modal">
                  {employees.map((e) => <option key={e.Emp_ID || e} value={e.Name || e} />)}
                </datalist>
              )}

              <table className="table table-bordered table-sm">
                <thead>
                  <tr>
                    <th style={{ width: 230 }}>Internal Part No</th>
                    <th style={{ width: 220 }}>Employee</th>
                    <th style={{ width: 180 }}>Date</th>
                    <th style={{ width: 140 }}>QTY</th>
                  </tr>
                </thead>
                <tbody>
                  {chunkModal.rows.map(({ key, qty, idx }) => {
                    const state = sched[key] || {
                      employee: "", date: todayISO, qty,
                      internalPartNo: chunkModal.r?.internalPart || "",
                    };
                    return (
                      <tr key={key}>
                        <td>
                          <input className="form-control form-control-sm" value={state.internalPartNo}
                            onChange={(e) => setField(key, "internalPartNo", e.target.value)}
                            placeholder="e.g., REFRAME MB -R" />
                        </td>
                        <td>
                          <input className="form-control form-control-sm" placeholder="Select or type"
                            list="employees_modal" value={state.employee}
                            onChange={(e) => {
                              const v = e.target.value;
                              setField(key, "employee", v);
                              if (idx === 0) {
                                setSched((prev) => {
                                  const next = { ...prev };
                                  chunkModal.rows.forEach(({ key: k }) => {
                                    next[k] = { ...(next[k] || {}), employee: v };
                                  });
                                  return next;
                                });
                              }
                            }} />
                        </td>
                        <td>
                          <input type="date" className="form-control form-control-sm" value={state.date}
                            onChange={(e) => {
                              const v = e.target.value;
                              setField(key, "date", v);
                              if (idx === 0) {
                                const series = businessSeries(v || todayISO, chunkModal.rows.length);
                                setSched((prev) => {
                                  const next = { ...prev };
                                  chunkModal.rows.forEach(({ key: k }, j) => {
                                    next[k] = { ...(next[k] || {}), date: series[j] };
                                  });
                                  return next;
                                });
                              }
                            }} />
                        </td>
                        <td>
                          <input type="number" min={0} step={1} className="form-control form-control-sm"
                            value={state.qty} placeholder={String(qty)}
                            onChange={(e) => setField(key, "qty", e.target.value)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="text-end d-flex gap-2 justify-content-end">
                <button className="btn btn-success btn-sm" onClick={handleScheduleAll}>Schedule All</button>
                <button className="btn btn-outline-secondary btn-sm" onClick={closeModal}>Done</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}